/**
 * Pure dressing plan for a themed dungeon: architecture (floors, stairs, ledges, walls) plus the
 * dressing layout (props, lights, decals). No three.js meshes; dungeon.ts turns it into draws and
 * the tests check it (walkable floor, spawns and doorways stay clear; deterministic).
 */
import type { DungeonDef } from '@keystone/sim';
import type { DungeonTheme } from '../themes/types';
import { VENOM_HEROES } from '../themes/venomAltar/heroes';
import { BARROW_HEROES } from '../themes/hollowBarrow/heroes'; // +field
import { type ArchLayout, buildArch } from './arch';
import { type DressLayout, type DressOptions, dressDungeon } from './dress';

/** Hero expanders by theme id. */
export const THEME_HEROES: Record<string, NonNullable<DressOptions['heroes']>> = { venom_altar: VENOM_HEROES, hollow_barrow: BARROW_HEROES };

/** Wall torch (all themed dungeons). */
export const WALL_TORCH = { asset: 'kd_torch_mounted', y: 1.9, off: 0, scale: 1.3, flame: [0.5, 0.75] as const };

export interface DungeonPlan {
  arch: ArchLayout;
  dress: DressLayout;
}

export function planDungeon(d: DungeonDef, theme: DungeonTheme): DungeonPlan {
  const arch = buildArch(d, theme, theme.extraHints?.(d) ?? []);
  // Doors keep their swing / slab space clear of clutter.
  const reserved = (d.doors ?? []).map((dr) => ({ pos: dr.position, r: dr.width / 2 + 1.5 }));
  const dress = dressDungeon(d, theme, arch, { heroes: THEME_HEROES[theme.id], reserved, torch: WALL_TORCH });
  return { arch, dress };
}
